import React, { useEffect } from "react";
import { View, ScrollView } from "react-native";
import { KeyboardAwareScrollView } from "@/components/ui/KeyboardAvoidingView";
import { Controller } from "react-hook-form";
import { useSafeAreaInsets } from "react-native-safe-area-context";
import { AppText } from "@/components/ui/AppText";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/Button";
import { AppTextInput } from "@/components/ui/TextInput";
import { useChangePasswordForm } from "@/features/auth/hooks/useChangePasswordForm";
import { toast } from "@/lib/toast";
import type { TranslationKeys } from "@chinese-vendor/i18n";
import { useIsWideScreen } from "@/hooks/useIsWideScreen";
import { Image } from "@/components/ui/Image";
import { PageFooter } from "@/components/ui/PageFooter";

const HERO_IMAGE = require("@assets/images/login-background-image.png");

// ─── Form content (shared mobile + web) ──────────────────────────────────────

function ChangePasswordForm() {
  const { t } = useI18n();

  const { form, isLoading, error, handleSubmit } = useChangePasswordForm();

  useEffect(() => {
    if (!error) return;

    toast.error({
      message: t(error as TranslationKeys),
    });
  }, [error, t]);

  return (
    <View className="items-start w-full">
      <AppText className="text-3xl text-dark-text-50 mb-2" weight="bold">
        {t("auth.changePassword.title")}
      </AppText>
      <AppText className="text-base text-dark-text-15 mb-8">
        {t("auth.changePassword.subtitle")}
      </AppText>

      <View className="w-full">
        <Controller
          control={form.control}
          name="new_password"
          render={({ field: { value, onChange, onBlur }, fieldState: { error: fieldError } }) => (
            <AppTextInput
              required
              label="auth.changePassword.newPasswordLabel"
              placeholder="auth.changePassword.newPasswordPlaceholder"
              value={value}
              onChangeText={onChange}
              onBlur={onBlur}
              secureTextEntry
              error={fieldError?.message as TranslationKeys | undefined}
            />
          )}
        />

        <AppText className="text-xs text-dark-text-15 -mt-2 mb-4">
          {t("auth.register.passwordHint")}
        </AppText>
      </View>

      <Controller
        control={form.control}
        name="confirm_new_password"
        render={({ field: { value, onChange, onBlur }, fieldState: { error: fieldError } }) => (
          <AppTextInput
            required
            label="auth.changePassword.confirmPasswordLabel"
            placeholder="auth.changePassword.confirmPasswordPlaceholder"
            value={value}
            onChangeText={onChange}
            onBlur={onBlur}
            secureTextEntry
            error={fieldError?.message as TranslationKeys | undefined}
          />
        )}
      />

      <Button
        className="w-full mt-3"
        label="auth.changePassword.submit"
        onPress={handleSubmit}
        loading={isLoading}
        variant="primary"
      />
    </View>
  );
}

// ─── Screen ───────────────────────────────────────────────────────────────────

export default function ChangePasswordScreen() {
  const insets = useSafeAreaInsets();
  const isWide = useIsWideScreen();

  // ── Wide-screen web layout (≥768px): split image + form ────────────────────
  if (isWide) {
    return (
      <View className="flex-1 bg-white min-h-full">
        <View className="flex-1 flex-row">
          {/* Left image panel */}
          <Image
            source={HERO_IMAGE}
            className="flex-1 bg-blue-90 max-w-xs lg:max-w-md"
            contentFit="cover"
          />

          {/* Right scrollable form */}
          <ScrollView
            className="flex-1 bg-white"
            contentContainerStyle={{ flexGrow: 1 }}
            showsVerticalScrollIndicator={false}
          >
            <View className="flex-1 items-center justify-start py-12 px-8">
              <View className="max-w-md w-full">
                <ChangePasswordForm />
              </View>
            </View>
            <PageFooter />
          </ScrollView>
        </View>
      </View>
    );
  }

  // ── Mobile layout ────────────────────────────────────────────────────────
  return (
    <KeyboardAwareScrollView
      contentContainerStyle={{ flexGrow: 1, paddingBottom: Math.max(insets.bottom, 24) }}
      className="bg-white py-4 tablet:py-page-y"
    >
      <View className="self-center w-full tablet:max-w-3xl px-page-x">
        <ChangePasswordForm />
      </View>
    </KeyboardAwareScrollView>
  );
}
